import { useMemo, useRef, useEffect } from "react";
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

// Register supported font families (besides Quill's defaults).
const Font = Quill.import("formats/font");
Font.whitelist = ["sans-serif", "serif", "monospace", "roboto"];
Quill.register(Font, true);

// Register size whitelist
const Size = Quill.import("formats/size");
Size.whitelist = ["small", false, "large", "huge"];
Quill.register(Size, true);

const TOOLBAR_OPTIONS = [
  [{ font: ["sans-serif", "serif", "monospace", "roboto"] }],
  [{ size: ["small", false, "large", "huge"] }],
  ["bold", "italic", "underline"],
  [{ color: [] }, { background: [] }],
  [{ align: "" }, { align: "center" }, { align: "right" }, { align: "justify" }],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link"],
  ["clean"],
];

/**
 * Rich text editor used inside Compose / Reply screens.
 * Includes a custom "UPPERCASE" button that uppercases the current selection
 * (or the entire content if no selection).
 */
export default function RichTextEditor({ value, onChange, placeholder = "Compose email…", testId = "rte" }) {
  const quillRef = useRef(null);

  const handleUppercase = () => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const range = editor.getSelection();
    if (range && range.length > 0) {
      const text = editor.getText(range.index, range.length);
      editor.deleteText(range.index, range.length, "user");
      editor.insertText(range.index, text.toUpperCase(), "user");
      editor.setSelection(range.index, text.length, "user");
    } else {
      const text = editor.getText();
      editor.setText(text.toUpperCase(), "user");
    }
    onChange(editor.root.innerHTML);
  };

  // Bind Cmd/Ctrl+Shift+U to uppercase shortcut once mounted
  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const root = editor.root;
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === "u" || e.key === "U")) {
        e.preventDefault();
        handleUppercase();
      }
    };
    root.addEventListener("keydown", handler);
    return () => root.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const modules = useMemo(() => ({ toolbar: TOOLBAR_OPTIONS }), []);

  return (
    <div data-testid={testId} className="rich-text-editor flex-1 flex flex-col min-h-0">
      <div className="flex items-center justify-between px-3 py-1 border-b border-stone-200 bg-stone-50">
        <span className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Editor</span>
        <button
          type="button"
          data-testid="rte-uppercase-btn"
          onClick={handleUppercase}
          title="UPPERCASE selection (⌘/Ctrl + Shift + U)"
          className="text-xs font-bold text-stone-600 hover:text-forest-700 hover:bg-forest-50 px-2 py-1 rounded"
        >
          AA
        </button>
      </div>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        modules={modules}
        placeholder={placeholder}
      />
    </div>
  );
}
